import { createScrollTrajectory, createTitleTravel } from './scroll-trajectory.js';
import { isIOSDevice } from './ios-device.js';

let lastInputTime = -Infinity;
let transitionActive = false;
let sampleActiveTravel = null;
// The scene can run before the scroll RAF. Sample the same trajectory at its
// presentation timestamp rather than rendering the previous DOM scroll offset.
export const sampleChapterScroll = (now, fallback) => sampleActiveTravel?.(now) ?? fallback;
export const isScrollTransitionActive = () => transitionActive;
export const isScrollInputActive = () => performance.now() - lastInputTime < 80;

export function createChapterScroll({beforeTravel} = {}) {
  let preparing = false, disposed = false;
  let frame = 0, target = scrollY, position = scrollY, touchY = null, touchX = null, touchConsumed = false;
  let startPosition = scrollY, startedAt = 0;
  let duration = 1500;
  let destinationIndex = 0;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 700px), (pointer: coarse)').matches;
  // CSS3D is fixed to the viewport. Safari need not scroll its long document
  // every frame just to transport a camera position already sampled by WebGL.
  const virtualScroll = isIOSDevice() && document.documentElement.classList.contains('world-sections-active');
  function setTraveling(active) {
    transitionActive = active;
    sampleActiveTravel = active ? now => trajectory(now - startedAt).position : null;
    document.documentElement.classList.toggle('camera-travel', active);
    if (mobile) document.documentElement.classList.toggle('mobile-camera-travel', active);
  }
  let trajectory = null;
  const blocked = target => document.querySelector('[role="dialog"], dialog[open]') || document.documentElement.dataset?.deviceRotating === 'true' || target.closest?.('input, textarea, select, [contenteditable="true"], [data-native-scroll]');
  const clamp = value => Math.max(0, Math.min(value, document.documentElement.scrollHeight - innerHeight));
  function tick(now) {
    const t = Math.min(1, Math.max(0, (now - startedAt) / duration));
    // A gentle ease-in, followed by equally smooth braking at the destination.
    const eased = t * t * t * (t * (t * 6 - 15) + 10);
    position = trajectory ? trajectory(now - startedAt).position : startPosition + (target - startPosition) * eased;
    if (!virtualScroll || t === 1) window.scrollTo({top: position, behavior: 'instant'});
    if (virtualScroll) {
      document.documentElement.classList.toggle('journey-scrolled', position > 0);
      window.dispatchEvent(new Event('codex-journey-frame'));
    }
    if (t < 1) frame = requestAnimationFrame(tick);
    else { frame = 0; setTraveling(false); lastGesture = -Infinity; }
  }
  function travel(destination, prepared = false) {
    const nextTarget = clamp(destination);
    if (preparing || disposed) return;
    if (!prepared && beforeTravel) {
      const pending = beforeTravel(nextTarget);
      if (pending?.then) {
        preparing = true;
        document.documentElement.classList.toggle('chapter-preparing', true);
        Promise.resolve(pending).catch(error => console.warn('Chapter preparation skipped', error)).finally(() => {
          preparing = false;
          if (disposed) return;
          document.documentElement.classList.toggle('chapter-preparing', false);
          travel(nextTarget, true);
        });
        return;
      }
    }
    if (frame && Math.abs(nextTarget - target) < .01) return;
    const now = performance.now();
    const previous = frame && trajectory ? trajectory(now - startedAt) : null;
    cancelAnimationFrame(frame);
    startPosition = position = previous?.position ?? scrollY;
    startedAt = now;
    target = nextTarget;
    const points = stops();
    destinationIndex = points.reduce((best, point, index) => Math.abs(point-target) < Math.abs(points[best]-target) ? index : best, 0);
    const studioStart = document.getElementById('about-story')?.offsetTop;
    const contactStart = document.getElementById('contact')?.offsetTop;
    const crossesStudioExit = studioStart != null && contactStart != null
      && Math.max(startPosition, target) > studioStart + 1
      && Math.min(startPosition, target) < contactStart - 1;
    const moonStart = document.getElementById('moon')?.offsetTop;
    const crossesMoon = moonStart != null && Math.max(startPosition, target) > moonStart - 4;
    const productStart = document.getElementById('products-showcase')?.offsetTop;
    const productEnd = document.getElementById('partners')?.offsetTop;
    const withinProducts = productStart != null && productEnd != null
      && Math.min(startPosition, target) >= productStart - 4
      && Math.max(startPosition, target) < productEnd;
    duration = withinProducts ? 950 : crossesMoon ? 1500 : crossesStudioExit ? 2000 : 1500;
    trajectory = createScrollTrajectory(startPosition, target, duration, previous?.velocity ?? 0, document.documentElement.scrollHeight - innerHeight);
    const titleStops = [
      [document.getElementById('solutions').offsetTop + innerHeight * .23, stops()[1]],
      [document.getElementById('products').offsetTop, document.getElementById('products-showcase').offsetTop],
      [document.getElementById('partners').offsetTop, document.getElementById('partners-showcase').offsetTop],
      [document.getElementById('about').offsetTop, document.getElementById('about-story').offsetTop],
    ];
    const waypoint = titleStops.find(([point, content]) => Math.abs(content-target) < 4 && point > Math.min(startPosition, target) + 4 && point < Math.max(startPosition, target) - 4)?.[0];
    if (waypoint != null && !previous) {
      duration = 2400;
      trajectory = createTitleTravel(startPosition, waypoint, target, duration);
    }
    if (reduced.matches) {
      setTraveling(false);
      frame = 0;
      position = target;
      window.scrollTo({top: target, behavior: 'instant'});
    } else {
      setTraveling(true);
      frame = requestAnimationFrame(tick);
    }
  }
  let lastGesture = -Infinity, lastDirection = 0;
  function stops() {
    const solutions = document.getElementById('solutions').offsetTop;
    const contact = document.getElementById('contact').offsetTop;
    const innovation = document.getElementById('innovation').offsetTop;
    const span = innovation - solutions;
    const products = document.getElementById('products-showcase').offsetTop;
    return [0, ...[38, 70, 102, 134].map(d => solutions + span * d / 180), innovation, products, document.getElementById('partners-showcase').offsetTop, document.getElementById('about-story').offsetTop, contact, document.getElementById('moon').offsetTop];
  }
  function advance(delta, freshPress = false) {
    if (!delta || frame || preparing) return;
    const now = performance.now(), direction = Math.sign(delta);
    lastInputTime = now;
    // Each camera journey owns its destination. Rearm wheel input at arrival
    // so continued scrolling does not need a pause or pointer movement.
    const fresh = freshPress || now - lastGesture > 220 || (Math.abs(delta) >= 2 && direction !== lastDirection);
    lastGesture = now;
    if (Math.abs(delta) < .5) return;
    lastDirection = direction;
    // A gesture owns one destination, including its decaying momentum events.
    // Further input is accepted only after the current trip has arrived.
    if (!fresh) return;
    const points = stops();
    const origin = frame && direction === Math.sign(target - position) ? target : sampleChapterScroll(now, scrollY);
    const next = direction > 0 ? points.find(p => p > origin + 4) : points.toReversed().find(p => p < origin - 4);
    if (next != null) {
      travel(next);
    }
  }
  function wheel(event) {
    if (event.ctrlKey || blocked(event.target)) return;
    event.preventDefault();
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? innerHeight : 1;
    advance(event.deltaY * unit);
  }
  function start(event) {
    touchY = touchX = null;
    touchConsumed = false;
    if (!blocked(event.target) && event.touches.length === 1) {
      lastGesture = -Infinity;
      touchY = event.touches[0].clientY;
      touchX = event.touches[0].clientX;
    }
  }
  function drag(event) {
    if (document.documentElement.dataset?.deviceRotating === 'true') { if (event.cancelable) event.preventDefault(); return; }
    if (event.touches.length !== 1) { touchY = touchX = null; return; }
    if (touchY == null || blocked(event.target)) return;
    // Claim the swipe on its first movement. Waiting for the gesture threshold
    // lets Safari start native momentum before the section animation takes over.
    if (event.cancelable) event.preventDefault();
    if (frame || preparing) { touchConsumed = true; return; }
    if (touchConsumed) return;
    const dy = touchY - event.touches[0].clientY;
    const dx = touchX - event.touches[0].clientX;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) return;
    if (Math.abs(dy) >= Math.abs(dx)) {
      touchConsumed = true;
      advance(dy, true);
    } else {
      // Lock horizontal gestures so a diagonal continuation cannot skip a section.
      touchConsumed = true;
      // Product selection belongs to the arrow buttons only.
    }
  }
  function resize() {
    // Mobile browser chrome changes the viewport during travel. Keep the
    // destination instead of cancelling the animation between sections.
    // Safari's toolbar can resize the viewport repeatedly during one swipe.
    // Retarget the same trip without resetting its clock/easing each time.
    if (frame) {
      const nextTarget = clamp(stops()[destinationIndex]);
      if (trajectory && Math.abs(nextTarget - target) > .01) {
        const now = performance.now(), current = trajectory(now - startedAt);
        duration = Math.max(1, duration - (now - startedAt));
        startPosition = position = current.position;
        startedAt = now;
        trajectory = createScrollTrajectory(startPosition, nextTarget, duration, current.velocity, document.documentElement.scrollHeight - innerHeight);
      }
      target = nextTarget;
    }
  }

  const end = () => { touchY = touchX = null; touchConsumed = false; };
  function anchor(event) {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link || (blocked(event.target) && !link.closest('.menu-dialog')) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const id = link.getAttribute('href').slice(1);
    const section = document.getElementById(id);
    if (!section || !['home','solutions','about','products','partners','contact','moon'].includes(id)) return;
    event.preventDefault();
    if (frame || preparing) return;
    const destinations = {solutions: stops()[1], products: document.getElementById('products-showcase').offsetTop, partners: document.getElementById('partners-showcase').offsetTop, about: document.getElementById('about-story').offsetTop};
    travel(destinations[id] ?? section.offsetTop);
  }
  function key(event) {
    if (blocked(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
    const delta = { ArrowDown: 100, PageDown: innerHeight * .85, ArrowUp: -100, PageUp: -innerHeight * .85 }[event.key];
    if (delta) { event.preventDefault(); if (!event.repeat) advance(delta, true); }
    else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault(); if (!frame && !preparing) travel(event.key === 'Home' ? 0 : document.documentElement.scrollHeight);
    }
  }

  const scroll = () => document.documentElement.classList.toggle('journey-scrolled', scrollY > 0);
  window.addEventListener('wheel',wheel,{passive:false});
  window.addEventListener('touchstart',start,{passive:true});
  window.addEventListener('touchmove',drag,{passive:false});
  window.addEventListener('touchend',end);
  window.addEventListener('touchcancel',end);
  window.addEventListener('resize',resize);
  document.addEventListener('click',anchor);
  window.addEventListener('keydown',key);
  window.addEventListener('scroll',scroll,{passive:true});
  scroll();
  return () => {
    disposed = true;
    document.documentElement.classList.toggle('chapter-preparing', false);
    if (virtualScroll && frame) window.scrollTo({top: sampleChapterScroll(performance.now(), scrollY), behavior: 'instant'});
    setTraveling(false);
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel',wheel);
    window.removeEventListener('touchstart',start);
    window.removeEventListener('touchmove',drag);
    window.removeEventListener('touchend',end);
    window.removeEventListener('touchcancel',end);
    window.removeEventListener('resize',resize);
    document.removeEventListener('click',anchor);
    window.removeEventListener('keydown',key);
    window.removeEventListener('scroll',scroll);
    document.documentElement.classList.remove('journey-scrolled');
  };
}
